import {Component, Inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatButton} from '@angular/material/button';
import {MatError, MatFormField, MatHint} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {FlexModule} from '@angular/flex-layout';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ManageIndustriesService} from '../manage-industries.service';
import {IndustryScopeType, SelectableCategory} from '../industry.model';

interface DialogData {
  offeringUuid: string;
  existingCodes: string[];
}

@Component({
  selector: 'app-add-category-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButton,
    MatFormField,
    MatInput,
    FlexModule,
    ReactiveFormsModule,
    MatHint,
    MatError
  ],
  templateUrl: './add-category-dialog.component.html',
  styleUrl: './add-category-dialog.component.scss'
})
export class AddCategoryDialogComponent {

  categoryForm: FormGroup;
  isLoading = false;

  constructor(
    private dialogRef: MatDialogRef<AddCategoryDialogComponent>,
    // @ts-ignore
    @Inject(MAT_DIALOG_DATA) private data: DialogData,
    private fb: FormBuilder,
    private industriesService: ManageIndustriesService
  ) {
    this.categoryForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      description: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(500)]]
      // Removed code field as it will be auto-generated
    });

    // Auto-generate code from name
    this.categoryForm.get('name')?.valueChanges.subscribe(name => {
      if (name && !this.categoryForm.get('code')?.touched) {
        const generatedCode = this.generateCodeFromName(name);
        // We don't set the code field directly since it's not in the form, but we'll use it in onSave
      }
    });
  }

  private generateCodeFromName(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 30);
  }

  private uniqueCodeValidator(control: any) {
    const value = control.value;
    if (value && this.data.existingCodes.includes(value)) {
      return {uniqueCode: true};
    }
    return null;
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    if (this.categoryForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formValue = this.categoryForm.value;
      // Generate code based on name and offering UUID
      const generatedCode = this.generateCodeFromName(formValue.name) + '-' + this.data.offeringUuid.substring(0, 8);

      const newCategory: SelectableCategory = {
        code: generatedCode,
        name: formValue.name,
        description: formValue.description,
        icon: 'settings',
        typeScope: IndustryScopeType.OFFERING,
        offeringUuid: this.data.offeringUuid,
        selected: true,
        industries: []
      };

      this.industriesService.saveOfferingCategory(newCategory).subscribe({
        next: (savedCategory) => {
          this.isLoading = false;
          // Convert to SelectableCategory format
          const selectableCategory: SelectableCategory = {
            ...savedCategory,
            selected: true,
            industries: []
          };
          this.dialogRef.close(selectableCategory);
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error saving category:', error);
          // Show error message
        }
      });
    }
  }

  getErrorMessage(fieldName: string): string {
    const field = this.categoryForm.get(fieldName);
    if (field?.hasError('required')) {
      return `${fieldName} is required`;
    }
    if (field?.hasError('minlength')) {
      return `${fieldName} must be at least 2 characters`;
    }
    if (field?.hasError('maxlength')) {
      return `${fieldName} must not exceed 500 characters`;
    }
    return '';
  }
}
